import {computed, inject, Injectable, NgZone, signal, WritableSignal} from '@angular/core';
import * as UtilityMediaAccess from '@serv/media_access/utility';
import {SnackbarService} from "@serv/snackbar.service";
//import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";

@Injectable({ providedIn: 'root' })

/**
 * @class DevicePermissionsService
 * @description
 * Servicio encargado de gestionar y verificar los permisos de acceso a dispositivos,
 * específicamente el micrófono, en el navegador. Utiliza la API de Permisos del navegador
 * para consultar el estado de los permisos y la API de MediaDevices para solicitar acceso.
 *
 * Proporciona señales reactivas para el estado del permiso y cualquier error asociado,
 * permitiendo que los componentes se suscriban a los cambios de estado.
 */
export class DevicePermissionsService{

  private _snackbarService = inject(SnackbarService);
  /**
   * @public
   * @property {WritableSignal<UtilityMediaAccess.PermissionState>} permissionState
   * @description Una señal reactiva que almacena el estado actual del permiso del micrófono.
   * Los posibles valores son 'prompt', 'granted', 'denied', 'unsupported'.
   * Inicialmente se establece en 'prompt'.
   */
  public readonly permissionState: WritableSignal<UtilityMediaAccess.PermissionState> = signal('prompt');
  /**
   * @private
   * @property {PermissionStatus | null} permissionStatus
   * @description Almacena el objeto `PermissionStatus` devuelto por la API de Permisos,
   * que permite escuchar cambios en el estado del permiso.
   */
  private permissionStatus: PermissionStatus | null = null;
  /**
   * @public
   * @property {WritableSignal<UtilityMediaAccess.MicrophoneError | null>} error
   * @description Una señal reactiva que almacena cualquier error que ocurra durante
   * la verificación o solicitud de permisos del micrófono. Es `null` si no hay errores.
   */
  public error: WritableSignal<UtilityMediaAccess.MicrophoneError | null> = signal(null);
  //private _console = inject(Tools_aiConsoleService);
  /**
   * @public
   * @property {Computed<boolean>} onChangePermissionRevoke
   * @description Una señal computada que indica si el permiso del micrófono no está 'granted'.
   * Es `true` si el permiso ha sido revocado o no ha sido concedido.
   */
  public readonly onChangePermissionRevoke= computed(()=>this.permissionState() !== 'granted')

  /**
   * @method checkMicrophonePermission
   * @description
   * Consulta el estado actual del permiso del micrófono utilizando la API de Permisos del navegador.
   * Actualiza `permissionState` y `error` con el resultado.
   * Configura un listener `onchange` para el estado del permiso para reaccionar a futuras modificaciones.
   * @returns {Promise<UtilityMediaAccess.PermissionState>} Una promesa que resuelve con el estado actual del permiso ('granted', 'denied', 'prompt', 'unsupported').
   */
  public async checkMicrophonePermission(): Promise<UtilityMediaAccess.PermissionState> {
    try {
      const permissionStatus = await navigator.permissions.query({name: 'microphone' as PermissionName});
      this.permissionState.set(permissionStatus.state);
      this.permissionStatus = permissionStatus;
      this.error.set(null);

      permissionStatus.onchange = () => {
        this.permissionState.set(permissionStatus.state);
       //this._console.log(`🔐 Permissão alterada para: ${permissionStatus.state}`);
      };
      return permissionStatus.state;
    } catch (err: any) {
      const error: UtilityMediaAccess.MicrophoneError = {name: err.name, message: err.message};
      this.error.set(error);
     //this._console.error('❌ Erro ao consultar permissão:', error);

      if (err.name === 'NotSupportedError') {
        this.permissionState.set('unsupported');
        this._snackbarService.error("AUDIOS.SPEECH_TO_TEXT_BROWSER_UNSUPORTED_ERROR");
        return 'unsupported';
      } else {
        this.permissionState.set('unsupported');
        this._snackbarService.error("AUDIOS.SPEECH_TO_TEXT_BROWSER_UNSUPORTED_ERROR");
        return 'unsupported';
      }
    }
  }

  /**
   * @method requestMicrophonePermission
   * @description
   * Intenta solicitar acceso al micrófono utilizando `navigator.mediaDevices.getUserMedia`.
   * Si el acceso es concedido, actualiza `permissionState` a 'granted'.
   * Si el acceso es denegado o hay un error, actualiza `permissionState` y `error` apropiadamente.
   * @returns {Promise<UtilityMediaAccess.PermissionState>} Una promesa que resuelve con 'granted' si el permiso es concedido, o se rechaza con el nuevo estado del permiso si hay un error.
   */
  public requestMicrophonePermission(): Promise<UtilityMediaAccess.PermissionState> {
    return new Promise<UtilityMediaAccess.PermissionState>(async (resolve, reject) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // If stream is obtained, permission was granted
        stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
        this.permissionState.set('granted');
        this.error.set(null);
        await this.checkMicrophonePermission(); // Re-check to configure onchange
        resolve('granted');
      } catch (err: any) {
        const error: UtilityMediaAccess.MicrophoneError = { name: err.name, message: err.message };
        this.error.set(error);
       //this._console.error('❌ Erro ao solicitar acesso ao microfone:', error);

        let newState: UtilityMediaAccess.PermissionState;
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          newState = 'denied';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          this._snackbarService.error("AUDIOS.SPEECH_TO_TEXT_BROWSER_UNSUPORTED_ERROR");
          newState = 'unsupported';
        } else {
          this._snackbarService.error("AUDIOS.SPEECH_TO_TEXT_BROWSER_UNSUPORTED_ERROR");
          newState = 'unsupported';
        }
        this.permissionState.set(newState);
        reject(newState); // Reject with the new state or re-throw the error
      }
    });
  }
}
